import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { Button, HStack, Modal, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationWarningModal = ({
  locations,
  restaurantSlug,
  onClose,
  onConfirm,
}: LocationWarningModalProps) => {
  const location = locations.find((el) => el.restaurantSlug === restaurantSlug);

  return (
    <Modal animationType="fade" size="medium" visible onRequestClose={onClose}>
      <LocationWarningContent
        locationWarningTitle={location?.warningDialogTitle ?? ''}
        locationWarningDescription={location?.warningDialogDescription ?? ''}
        restaurantSlug={restaurantSlug}
        restaurantId={location?.id ?? ''}
        restaurantName={location?.name ?? ''}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    </Modal>
  );
};

export const LocationWarningContent = ({
  locationWarningTitle,
  locationWarningDescription,
  restaurantSlug,
  restaurantId,
  restaurantName,
  onClose,
  onConfirm,
}: LocationWarningContentProps) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();

  const onChangeLocation = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleConfirm = React.useCallback(() => {
    if (!onConfirm || !restaurantSlug) return;
    onConfirm({
      restaurantId: restaurantId ?? '',
      restaurantSlug: restaurantSlug ?? '',
      restaurantName: restaurantName ?? '',
    });
  }, [onConfirm, restaurantSlug, restaurantId, restaurantName]);

  // ─── Styles ──────────────────────────────────────────────────────

  const containerStyles = [styles.container, match([undefined, styles.containerSM])];
  const infoBlockStyles = match([styles.infoXS, styles.infoSM]);
  const buttonSize = match(['medium', 'large'] as const);
  const buttonStyles = match([undefined, styles.buttonSM]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView edges={SAFE_AREA_EDGES}>
      <View style={containerStyles} testID="location-warning-modal">
        <View style={infoBlockStyles}>
          <Modal.Headline>{locationWarningTitle}</Modal.Headline>
          <Modal.BodyText>{locationWarningDescription}</Modal.BodyText>
        </View>

        <Modal.Footer withoutTopBorder>
          <HStack style={styles.buttonsStack} itemsPerRow={currentBreakpoint.isXS ? 2 : undefined}>
            <Button
              style={buttonStyles}
              size={buttonSize}
              palette="secondary"
              onPress={onChangeLocation}
              accessibilityLabel={formatMessage(messages.locationWarningModalCtaChange)}
              accessibilityHint={formatMessage(messages.locationWarningModalCtaChange)}
            >
              {formatMessage(messages.locationWarningModalCtaChange)}
            </Button>

            <Button
              style={buttonStyles}
              size={buttonSize}
              onPress={handleConfirm}
              accessibilityLabel={formatMessage(messages.locationWarningModalCtaConfirm)}
              accessibilityHint={formatMessage(messages.locationWarningModalCtaConfirm)}
              testID="location-warning-modal.confirm"
            >
              {formatMessage(messages.locationWarningModalCtaConfirm)}
            </Button>
          </HStack>
        </Modal.Footer>
      </View>
    </SafeAreaView>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationWarningModalCtaChange: {
    defaultMessage: 'Change location',
    description: 'Location | Warning Modal | Cta Change',
  },
  locationWarningModalCtaConfirm: {
    defaultMessage: 'Yes, I am',
    description: 'Location | Warning Modal | Cta Confirm',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  default: ['bottom'],
  ios: [],
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationWarningModalProps = Readonly<{
  locations: ReadonlyArray<
    Partial<
      Readonly<{
        id: string;
        name: string;
        restaurantSlug: string;
        warningDialogTitle?: string;
        warningDialogDescription?: string;
      }>
    >
  >;
  restaurantSlug: string;
  onClose: () => void;
  onConfirm: (props: {
    restaurantId: string;
    restaurantSlug: string;
    restaurantName: string;
  }) => void;
}>;

type LocationWarningContentProps = Readonly<{
  locationWarningTitle: string;
  locationWarningDescription: string;
  restaurantSlug: string;
  restaurantId: string;
  restaurantName: string;
  onClose: () => void;
  onConfirm: (props: {
    restaurantId: string;
    restaurantSlug: string;
    restaurantName: string;
  }) => void;
}>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['2'],
    paddingBottom: theme.spacing['4'],
  },
  containerSM: {
    paddingTop: theme.spacing['6'],
  },
  infoXS: {
    marginTop: theme.spacing['4'],
  },
  infoSM: {
    marginVertical: theme.spacing['4'],
  },
  buttonsStack: {
    justifyContent: 'center',
  },
  buttonSM: {
    minWidth: 187,
    paddingHorizontal: theme.spacing['2'],
  },
});
